import { Graphics } from 'pixi.js';

import type { WorldModel } from '../world/model';
import {
  tileToIso,
  TILE_HEIGHT_PX,
  TILE_WIDTH_PX,
  visibleTileRange,
  type CameraState,
} from './iso';
import type { SceneLayers } from './scene';

export const CLIFF_THRESHOLD_U8 = 13;

export function isCliffEdge(
  elevA: number,
  elevB: number,
  threshold: number,
): boolean {
  return Math.abs(elevA - elevB) > threshold;
}

export interface CliffEdgeRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ) => void;
  destroy: () => void;
}

export function createCliffEdgeRenderer(layers: SceneLayers): CliffEdgeRenderer {
  const g = new Graphics();
  layers.floorOverlayLayer.addChild(g);

  const halfW = TILE_WIDTH_PX / 2;
  const halfH = TILE_HEIGHT_PX / 2;

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ): void => {
    g.clear();
    if (model.width === 0 || model.height === 0) return;
    if (model.tileElevations.length === 0) return;

    const r = visibleTileRange(cam, viewportW, viewportH, 2);
    const minX = Math.max(r.minX, 0);
    const minY = Math.max(r.minY, 0);
    const maxX = Math.min(r.maxX, model.width - 1);
    const maxY = Math.min(r.maxY, model.height - 1);
    const w = model.width;
    const h = model.height;

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const idx = y * w + x;
        const elev = model.tileElevations[idx]!;
        const { x: px, y: py } = tileToIso(x, y);

        // North edge (toward x, y-1)
        if (y > 0 && isCliffEdge(elev, model.tileElevations[(y - 1) * w + x]!, CLIFF_THRESHOLD_U8)) {
          g.moveTo(px, py - halfH);
          g.lineTo(px + halfW, py);
          g.stroke({ color: 0x2a2a2a, alpha: 0.6, width: 2 });
        }

        // East edge (toward x+1, y)
        if (x < w - 1 && isCliffEdge(elev, model.tileElevations[y * w + x + 1]!, CLIFF_THRESHOLD_U8)) {
          g.moveTo(px + halfW, py);
          g.lineTo(px, py + halfH);
          g.stroke({ color: 0x2a2a2a, alpha: 0.6, width: 2 });
        }

        // South edge (toward x, y+1)
        if (y < h - 1 && isCliffEdge(elev, model.tileElevations[(y + 1) * w + x]!, CLIFF_THRESHOLD_U8)) {
          g.moveTo(px, py + halfH);
          g.lineTo(px - halfW, py);
          g.stroke({ color: 0x2a2a2a, alpha: 0.6, width: 2 });
        }

        // West edge (toward x-1, y)
        if (x > 0 && isCliffEdge(elev, model.tileElevations[y * w + x - 1]!, CLIFF_THRESHOLD_U8)) {
          g.moveTo(px - halfW, py);
          g.lineTo(px, py - halfH);
          g.stroke({ color: 0x2a2a2a, alpha: 0.6, width: 2 });
        }
      }
    }
  };

  return {
    render,
    destroy: () => {
      g.destroy();
    },
  };
}
